import { useAuth } from '@/components/auth/authProvider';
import { createUserFullName } from '@/helpers/createUserFullName';
import { getPhoneNumberUpdate } from '@/helpers/getPhoneNumberUpdate';
import { Box, toast, Spinner } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import { getUserFacingErrorMessage } from '../../services/helpers';
import { useEditCurrentUserMutation, useGetCurrentUserQuery } from '../../services/users';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { YourProfileForm } from './YourProfileForm';

export const EditProfile = () => {
  const { login } = useAuth();
  const { data: currentUser, isLoading } = useGetCurrentUserQuery();
  const [editProfile] = useEditCurrentUserMutation();

  const initialValues = useMemo(
    () => ({
      givenName: currentUser?.givenName,
      familyName: currentUser?.familyName,
      phoneNumber: currentUser?.phoneNumber,
      userIsSalesRep: currentUser?.userIsSalesRep || false,
    }),
    [currentUser],
  );

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      setSubmitting(true);
      // yup's .trim() only transforms during validation, so trim explicitly before sending.
      await editProfile({
        givenName: values.givenName?.trim(),
        familyName: values.familyName?.trim(),
        userIsSalesRep: values.userIsSalesRep,
        ...getPhoneNumberUpdate(initialValues.phoneNumber, values.phoneNumber),
      }).unwrap();
      toast.success('Profile modified successfully');
      await login(JSON.stringify({ returnTo: '/profile' }));
    } catch (e: unknown) {
      toast.error(getUserFacingErrorMessage(e, 'Error updating profile'));
    }
    setSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Edit Profile</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          title="Your Profile"
          eyebrow={
            <Link to={`/profile`}>
              {createUserFullName(currentUser?.givenName, currentUser?.familyName) || '...'}
            </Link>
          }
        />
        <Box padding={{ base: '0 lg', tablet: '0' }}>
          {!isLoading ? (
            <YourProfileForm
              initialValues={initialValues}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
              cancelButtonUrl={'/profile'}
            />
          ) : (
            <Spinner size="lg" />
          )}
        </Box>
      </MainContainer>
    </>
  );
};
